import { defaultColumns } from 'bpm/pages/TablePage/tablePage'

export interface CurrentDropDownInput {
    filterName: string
    inputValue: string
}

export const PER_PAGE = 150
export const OPTIMISTIC_CARDS = 5

export const emptySelectionObject: Record<string, string[]> = {
    noteReasons: [],
    categories: [],
    listingLevels: [],
    partnerships: [],
    submissionStatus: [],
    isActive: [],
    displayNameWithFirmName: [],
    sectionNames: [],
    additionalContent: [],
    sectionNameWithProductType: [],
    isDeltekWriterDraft: [],
}

export const emptyInputSelectionObject: Record<string, Date | string | null> = {
    lastUpdatedEndDate: null,
    lastUpdatedStartDate: null,
    lastUpdatedInPastDays: null,
}
export const defaultSortOrder =
    'sectionNumber,productType,type desc,lastUpdated desc'
export const defaultQuery =
    'groupby=sectionNumber false,productType false&sortby=sectionNumber,productType,type desc,lastUpdated desc&defaultFilter=true&showBackButton=true'
export const defaultfilterParam = 'df'

export const defaultTableSettings = {
    isTableGroupingEnabled: true,
    isTableView: true,
    selectedColumns: [
        {
            columnName: 'groupColExpander',
            order: 1,
            columnOrder: 'UNDEFINED',
            columnWidth: 300,
            isColumnPinned: false,
        },
        ...defaultColumns.map((col, index) => {
            return {
                columnName: col.id,
                order: index + 2,
                columnOrder: 'UNDEFINED',
                columnWidth: 300,
                isColumnPinned: false,
            }
        }),
    ],
    tableGroupings: [
        {
            columnName: 'sectionName',
            isCollapsed: false,
        },
        {
            columnName: 'productType',
            isCollapsed: false,
        },
    ],
}

export const tableSettingsState = {
    filters: [],
    availableColumns: [
        { id: 'displayNameWithFirmName', title: 'Company Full Name' },
        { id: 'model', title: 'Product Model Number' },
        { id: 'productName', title: 'Product Name' },
        { id: 'submissionStatus', title: 'State' },
        { id: 'firmDisplayName', title: 'Company Display Name' },
        { id: 'firmId', title: 'Company ID' },
        { id: 'firmLogoUrl', title: 'Company Logo URL' },
        { id: 'specAgentProductTypeId', title: 'SA Product Type ID' },
        { id: 'partnershipLinksSM', title: 'Sustainable Minds' },
        { id: 'partnershipLinksULE', title: 'ULE' },
        { id: 'partnershipLinksBIM', title: 'BIMsmith Links' },
        { id: 'hasManufacturerOptions', title: 'Has Manufacturer Options' },
        { id: 'hasProductDetailNotes', title: 'Notes' },
        { id: 'contentLibrary', title: 'Product Family' },
        { id: 'fullLengthDoc', title: 'Full Length Doc' },
        { id: 'updatedLastAiScrapeDate', title: 'Last Scraped' },
        { id: 'canAutoGenerateProductSpecification', title: 'Auto Generate' },
        { id: 'description', title: 'Description' },
        { id: 'assignedBPMUsers', title: 'Assigned BPM' },
        { id: 'assignedDeltekUsers', title: 'Assigned Deltek' },
        { id: 'assignedContactUsers', title: 'Product Contact' },
        { id: 'importCatalog', title: 'Import Catalog' },
    ],
    groupColumns: [
        { id: 'sectionName', title: 'Family Name' },
        { id: 'isActive', title: 'Status' },
        { id: 'productType', title: 'Product Type Name' },
    ],
    columnWidths: {
        groupColExpander: 104,
        type: 300,
        firmName: 300,
        categories: 360,
        listingId: 140,
        familyNumber: 300,
        canAutoGenerateProductSpecification: 300,
        lastUpdated: 300,
    },
    pinningProps: { firstColumnPinned: false, lastColumnPinned: false },
    orderBy: 'sectionName,isActive,productType,type,lastUpdated desc',
    selectedColumns: [
        { id: 'type', title: 'Listing Type' },
        { id: 'firmName', title: 'Company Name' },
        { id: 'categories', title: 'Category' },
        { id: 'listingId', title: 'Listing ID' },
        { id: 'familyNumber', title: 'Family Number' },
        { id: 'canAutoGenerateProductSpecification', title: 'Auto Generate' },
        { id: 'lastUpdated', title: 'Last Updated' },
    ],
    groupEnabled: true,
    collapseColumnChecked: {
        sectionName: false,
        isActive: false,
        productType: false,
    },
    lastUpdated: 'Wed Feb 21 2024 03:34:59 GMT+0800 (China Standard Time)',
    isTableView: true,
}

// Note: commented the facets that are under text search in the table.
// If we want to use the facets, we need to implement an autosuggest for each filter
export const validFacets = [
    // 'firmName',
    // 'displayNameWithFirmName',
    // 'productType',
    // 'productName',
    // 'categories',
    'listingLevels',
    'submissionStatus',
    'isActive',
    // 'partnerships',
    // 'sectionNames',
    // 'sectionNameWithProductType',
    // 'assignedDeltekUsers',
    // 'hasBimLinks',
    // 'hasCatalogs',
    // 'hasDocumentFL',
    // 'hasDocumentSF',
    'sectionNumber',
    'specAgentProductTypeId',
    'specAgentManufacturerId',
    'specAgentListingMaster',
    'specAgentDocType',
    'sectionNameWithProductTypeWithProductTypeId',
    'sectionNameWithProductTypeWithPremiumPlus',
    'sectionNameWithProductTypeWithPremiumPlusWithPtid',
    'isDeltekWriterDraft',
    'isPremiumPlus',
    'type',
    'withManufacturerOption',
    'contentLibrary',
]

export const validOrderBy = [
    'sectionNumber',
    'productType',
    'firmDisplayName',
    'submissionStatus',
    'isActive',
    'type',
    'lastUpdated',
]

export const facetIdMap: Record<string, string> = {
    type: 'listingLevels',
    firmName: 'firmName',
    displayNameWithFirmName: 'displayNameWithFirmName',
    productType: 'productType',
    productName: 'productName',
    categories: 'categories',
    submissionStatus: 'submissionStatus',
    isActive: 'isActive',
    sectionName: 'sectionNames',
    familyNumber: 'sectionNumber',
    specAgentProductTypeId: 'specAgentProductTypeId',
    hasManufacturerOptions: 'withManufacturerOption',
    contentLibrary: 'contentLibrary',
    // Note: Facets Not included in the Table View Yet
    // 'sectionNameWithProductType',
    // 'assignedDeltekUsers',
    // 'hasBimLinks',
    // 'hasCatalogs',
    // 'hasDocumentFL',
    // 'hasDocumentSF',
    // "partnerships"
    // 'specAgentManufacturerId',
    // 'specAgentListingMaster',
    // 'specAgentDocType',
    // 'sectionNameWithProductTypeWithProductTypeId',
    // 'sectionNameWithProductTypeWithPremiumPlus',
    // 'sectionNameWithProductTypeWithPremiumPlusWithPtid',
    // 'isDeltekWriterDraft',
    // 'isPremiumPlus',
}

export const setTableSettingsResponse = {
    setTableVewSetting: {
        selectedColumns: [
            {
                columnName: 'groupColExpander',
                columnWidth: 300,
                order: 1,
                isColumnPinned: false,
                columnOrder: 'UNDEFINED',
                __typename: 'SelectedColumn',
            },
            {
                columnName: 'displayNameWithFirmName',
                columnWidth: 300,
                order: 2,
                isColumnPinned: false,
                columnOrder: 'DESCENDING',
                __typename: 'SelectedColumn',
            },
            {
                columnName: 'familyNumber',
                columnWidth: 300,
                order: 3,
                isColumnPinned: false,
                columnOrder: 'UNDEFINED',
                __typename: 'SelectedColumn',
            },
            {
                columnName: 'model',
                columnWidth: 300,
                order: 4,
                isColumnPinned: false,
                columnOrder: 'UNDEFINED',
                __typename: 'SelectedColumn',
            },
            {
                columnName: 'productName',
                columnWidth: 300,
                order: 5,
                isColumnPinned: false,
                columnOrder: 'UNDEFINED',
                __typename: 'SelectedColumn',
            },
            {
                columnName: 'type',
                columnWidth: 300,
                order: 6,
                isColumnPinned: false,
                columnOrder: 'DESCENDING',
                __typename: 'SelectedColumn',
            },
            {
                columnName: 'isActive',
                columnWidth: 300,
                order: 7,
                isColumnPinned: false,
                columnOrder: 'UNDEFINED',
                __typename: 'SelectedColumn',
            },
            {
                columnName: 'submissionStatus',
                columnWidth: 300,
                order: 8,
                isColumnPinned: false,
                columnOrder: 'UNDEFINED',
                __typename: 'SelectedColumn',
            },
        ],
        isTableGroupingEnabled: true,
        tableGroupings: [
            {
                columnName: 'sectionName',
                groupingOrder: 0,
                isPinned: false,
                isCollapsed: false,
                __typename: 'TableViewGroupingSetting',
            },
            {
                columnName: 'productType',
                groupingOrder: 0,
                isPinned: false,
                isCollapsed: false,
                __typename: 'TableViewGroupingSetting',
            },
        ],
        isTableView: true,
        userId: '',
        __typename: 'TableViewSetting',
    },
}
